import i18nParent from 'i18next'
import type { i18n as I18nParentInterface, InitOptions, Module, ParseKeys } from 'i18next'
import Config from 'APP/Config'
import Moment from 'moment-timezone'
import { getLocales } from 'react-native-localize'
import 'moment/locale/fr'
import 'moment/locale/de'
import ChainedBackend from 'i18next-chained-backend'
import HttpBackend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'

import EN from 'APP/I18n/en.json'
import FR from 'APP/I18n/fr.json'

import { isWeb } from 'APP/Helpers/checkPlatform'
import { getBrandVersionedI18n } from 'APP/Helpers/brandVersionedUtils'
import { type SupportedLocales } from 'APP/Models/Locale'

interface I18n extends I18nParentInterface {
  /**
   * This method gets the system locale of a member's device. If the system locale is not supported, it will return the default 'en' locale
   */
  baseLocale: SupportedLocales
  /**
   * This method gets the system locale of a member's device. Use with caution, as it may not be the same as the baseLocale.
   */
  locale: string
  communicationLanguage: 'english' | 'french'
  serverString: (serverStringObj: { en?: string; fr?: string }) => string
}

const i18n = i18nParent as I18n

const locale = getLocales()[0].languageCode || ''

const languageDetector = isWeb()
  ? (LanguageDetector as unknown as I18nParentInterface)
  : ({
      init: Function.prototype,
      type: 'languageDetector',
      detect: () => locale,
      cacheUserLanguage: Function.prototype,
    } as unknown as I18nParentInterface)

const getResources = () => {
  const sources: Record<string, unknown> = {
    en: EN,
    fr: FR,
  }

  let resources: Record<string, { translation: object }> = {}
  Config.SUPPORTED_LANGUAGES.forEach((lang) => {
    resources[lang] = {
      translation: getBrandVersionedI18n(
        lang,
        sources[lang] as Record<string, unknown>,
        Config.BRAND_VERSION
      ),
    }
  })

  return resources
}
const resources = getResources()

let commonOptions = {
  fallbackLng: Config.DEFAULT_LANGUAGE,
  supportedLngs: Config.SUPPORTED_LANGUAGES,
  preload: Config.SUPPORTED_LANGUAGES,
  initImmediate: false,
  ns: ['translation', 'services-and-options', 'health-issue-types'],
  backend: {
    backends: [resourcesToBackend(resources), HttpBackend],
    backendOptions: [
      null,
      { loadPath: `${Config.CMS.baseUrl}/translations/{{ns}}/?locale={{lng}}` },
    ],
  },
}

const initOptions = isWeb()
  ? {
      ...commonOptions,
      detection: {
        order: ['querystring', 'navigator'],
        convertDetectedLanguage: (lng: string) => lng.slice(0, 2),
      },
      whitelist: Config.SUPPORTED_LANGUAGES,
    }
  : {
      ...commonOptions,
      compatibilityJSON: 'v3', // Use old plurals behavior TODO: migrate to new version https://www.i18next.com/misc/migration-guide#v20.x.x-to-v21.0.0
    }

i18n
  .use(languageDetector as unknown as Module)
  .use(ChainedBackend)
  .init(initOptions as InitOptions)

i18n.locale = isWeb() ? i18n.language : locale

// Define global string replacements
i18n.options.interpolation!.defaultVariables = {
  productName: i18n.t('ProductName'),
}

// This method gets the system locale of a member's device. If the system locale is not supported, it will return the default 'en' locale.
Object.defineProperty(i18n, 'baseLocale', {
  get: function () {
    if (isWeb()) return i18n.language

    const languages = Object.keys(resources)
    const defaultLocale = Config.DEFAULT_LANGUAGE

    if (languages.includes(locale)) {
      return locale
    }

    if (defaultLocale && languages.includes(defaultLocale)) {
      return defaultLocale
    }

    return languages[0]
  },
})

Moment.locale(i18n.baseLocale)

const communicationLanguage = i18n.baseLocale === 'fr' ? 'french' : 'english'
i18n.communicationLanguage = communicationLanguage

i18n.serverString = (serverStringObj: { en?: string; fr?: string }) => {
  return (i18n.baseLocale === 'fr' ? serverStringObj?.fr : serverStringObj?.en) ?? ''
}

export type TranslationKey = ParseKeys

export const isTranslationKey = (key: string): key is TranslationKey => i18n.exists(key)

export default i18n
